import { logError } from '@ptek/client-logger';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { documentsClient } from '../../../api-clients/documents';
import { AppContext } from '../../../context/AppContext';
import { DocumentState } from '../../../types/document';

interface UseDocumentAutoSaveProps {
  documentState: DocumentState | null;
}

interface UseDocumentAutoSaveReturn {
  saveNow: () => Promise<void>;
  debouncedSave: (content: Record<string, unknown>) => void;
}

const SAVE_DELAY_MS = 800;

function canSaveDocument(documentState: DocumentState | null): boolean {
  return Boolean(
    documentState?.id &&
      !documentState.id.startsWith('generating-') &&
      !documentState.id.startsWith('error-')
  );
}

export function useDocumentAutoSave({
  documentState,
}: UseDocumentAutoSaveProps): UseDocumentAutoSaveReturn {
  const { updateSessionModel } = useContext(AppContext);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const executeSave = useCallback(
    async (content: Record<string, unknown>) => {
      if (!canSaveDocument(documentState)) return;

      try {
        await documentsClient.updateDocument(documentState!.id, {
          generatedContent: content,
        });

        const saveTime = new Date();

        updateSessionModel((prev) => ({
          ...prev,
          documents: prev.documents.map((doc) =>
            doc.id === documentState!.id
              ? {
                  ...doc,
                  content:
                    typeof content === 'string'
                      ? content
                      : JSON.stringify(content),
                  lastSaved: saveTime.toISOString(),
                }
              : doc
          ),
        }));
      } catch (error) {
        logError('useDocumentAutoSave', 'Failed to save document:', error);
      }
    },
    [documentState, updateSessionModel]
  );

  const saveNow = useCallback(async () => {
    if (
      !canSaveDocument(documentState) ||
      !documentState?.content ||
      !documentState.isModified
    ) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    await executeSave(documentState.content);
  }, [documentState, executeSave]);

  const debouncedSave = useCallback(
    (content: Record<string, unknown>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        executeSave(content);
      }, SAVE_DELAY_MS);
    },
    [executeSave]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    saveNow,
    debouncedSave,
  };
}
